import React, { useMemo } from 'react';
import { LayoutGrid, Plus, Trash2 } from 'lucide-react';
import './HeroHomeEditor.css';

function deepClone(o) {
  try {
    return JSON.parse(JSON.stringify(o || {}));
  } catch {
    return {};
  }
}

function mergeNewsContent(comp, changes) {
  const raw =
    typeof comp.content === 'string'
      ? JSON.parse(comp.content || '{}')
      : deepClone(comp.content);
  const ch = changes[comp.id] || {};
  Object.entries(ch).forEach(([pathStr, val]) => {
    const parts = pathStr.split('.');
    let cur = raw;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      const next = parts[i + 1];
      if (cur[p] == null) cur[p] = /^\d+$/.test(String(next)) ? [] : {};
      cur = cur[p];
    }
    cur[parts[parts.length - 1]] = val;
  });
  return raw;
}

const emptyItem = (id) => ({
  id,
  title: '',
  date: '',
  description: '',
});

export default function NewsPageEditor({
  component: comp,
  languages,
  changes,
  onUpdate,
}) {
  const langCodes = (languages || []).map((l) => l.code);
  const codes = langCodes.length ? langCodes : ['en', 'ru', 'uz'];

  const content = useMemo(
    () => mergeNewsContent(comp, changes),
    [comp, changes],
  );

  const setHeader = (lang, field, value) => {
    onUpdate(comp.id, [lang, 'header', field], value);
  };

  const itemsByLang = (code) => {
    const arr = content[code]?.items;
    return Array.isArray(arr) ? arr : [];
  };

  const addItem = () => {
    let maxId = 0;
    codes.forEach((c) => {
      (itemsByLang(c) || []).forEach((it) => {
        const x = Number(it?.id);
        if (Number.isFinite(x)) maxId = Math.max(maxId, x);
      });
    });
    const id = maxId + 1;
    codes.forEach((code) => {
      const cur = [...itemsByLang(code), emptyItem(id)];
      onUpdate(comp.id, [code, 'items'], cur);
    });
  };

  const removeItem = (index) => {
    codes.forEach((code) => {
      const cur = [...itemsByLang(code)];
      cur.splice(index, 1);
      onUpdate(comp.id, [code, 'items'], cur);
    });
  };

  const setItemField = (lang, index, field, value) => {
    const cur = [...itemsByLang(lang)];
    if (!cur[index]) cur[index] = emptyItem(index + 1);
    cur[index] = { ...cur[index], [field]: value };
    onUpdate(comp.id, [lang, 'items'], cur);
  };

  const maxItemsLen = Math.max(...codes.map((c) => itemsByLang(c).length), 0);

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <LayoutGrid size={22} />
        <div>
          <h3 className="hero-home-editor__title">Страница News</h3>
          <p className="hero-home-editor__hint">
            Заголовок и подзаголовок — на каждый язык. Карточки новостей редактируются
            по языкам с одинаковым количеством записей.
          </p>
        </div>
      </div>

      <section className="hero-home-section">
        <h4>Заголовок страницы</h4>
        <div className="hero-lang-grid">
          {codes.map((code) => (
            <React.Fragment key={code}>
              <label className="hero-lang-field">
                <span className="hero-lang-code">{code.toUpperCase()}</span>
                <input
                  type="text"
                  value={content[code]?.header?.title ?? ''}
                  onChange={(e) => setHeader(code, 'title', e.target.value)}
                  placeholder="Заголовок"
                />
              </label>
              <label className="hero-lang-field">
                <span className="hero-lang-code">{code.toUpperCase()}</span>
                <input
                  type="text"
                  value={content[code]?.header?.subtitle ?? ''}
                  onChange={(e) => setHeader(code, 'subtitle', e.target.value)}
                  placeholder="Подзаголовок"
                />
              </label>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="hero-home-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <h4 style={{ margin: 0 }}>Новости</h4>
          <button type="button" className="hero-media-open-modal" onClick={addItem}>
            <Plus size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Добавить карточку
          </button>
        </div>

        {maxItemsLen === 0 ? (
          <p className="hero-media-modal-hint" style={{ marginTop: '0.75rem' }}>
            Пока нет новостей — нажмите «Добавить карточку».
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginTop: '0.75rem' }}>
            {Array.from({ length: maxItemsLen }).map((_, idx) => (
              <div
                key={idx}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '1rem',
                  background: '#fafafa',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <strong style={{ fontSize: '0.9rem' }}>Карточка #{idx + 1}</strong>
                  <button
                    type="button"
                    className="hero-media-clear"
                    style={{ width: 'auto', padding: '0.35rem 0.65rem' }}
                    onClick={() => removeItem(idx)}
                  >
                    <Trash2 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Удалить
                  </button>
                </div>
                {codes.map((code) => (
                  <div key={code} style={{ marginBottom: '0.75rem' }}>
                    <div className="hero-lang-code" style={{ marginBottom: '0.35rem' }}>
                      {code.toUpperCase()}
                    </div>
                    <div className="hero-button-block">
                      <input
                        type="text"
                        placeholder="Заголовок"
                        value={itemsByLang(code)[idx]?.title ?? ''}
                        onChange={(e) => setItemField(code, idx, 'title', e.target.value)}
                        style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
                      />
                      <input
                        type="text"
                        placeholder="Дата (произвольная строка)"
                        value={itemsByLang(code)[idx]?.date ?? ''}
                        onChange={(e) => setItemField(code, idx, 'date', e.target.value)}
                        style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
                      />
                      <textarea
                        rows={3}
                        placeholder="Описание"
                        value={itemsByLang(code)[idx]?.description ?? ''}
                        onChange={(e) =>
                          setItemField(code, idx, 'description', e.target.value)
                        }
                        style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
